// Hogwarts theme — the four house palettes plus the shared castle colours.
//
// Wired through styled-components' ThemeProvider in App.tsx, so any component
// can read `props.theme`. `buildTheme(house)` lets the whole site be re-skinned
// to a different house from one place.

export type HouseName = "gryffindor" | "slytherin" | "ravenclaw" | "hufflepuff";

export interface House {
  name: string;
  crest: string;
  primary: string;
  secondary: string;
  glow: string;
  trait: string;
}

export const HOUSES: Record<HouseName, House> = {
  gryffindor: {
    name: "Gryffindor",
    crest: "\u{1F981}",
    primary: "#ae0001",
    secondary: "#d3a625",
    glow: "rgba(211, 166, 37, 0.35)",
    trait: "Courage, daring, nerve and chivalry",
  },
  slytherin: {
    name: "Slytherin",
    crest: "\u{1F40D}",
    primary: "#1a472a",
    secondary: "#aaaaaa",
    glow: "rgba(42, 157, 89, 0.35)",
    trait: "Ambition, cunning and resourcefulness",
  },
  ravenclaw: {
    name: "Ravenclaw",
    crest: "\u{1F985}",
    primary: "#222f5b",
    secondary: "#946b2d",
    glow: "rgba(93, 128, 200, 0.35)",
    trait: "Wit, learning and wisdom",
  },
  hufflepuff: {
    name: "Hufflepuff",
    crest: "\u{1F9A1}",
    primary: "#ecb939",
    secondary: "#372e29",
    glow: "rgba(236, 185, 57, 0.35)",
    trait: "Patience, loyalty and hard work",
  },
};

/* Shared castle palette — unchanged by whichever house you're sorted into. */
export const castle = {
  bg: "#17161a",
  bgDeep: "#141317",
  bgCard: "rgba(20, 14, 28, 0.7)",
  bgCardHover: "rgba(32, 22, 44, 0.8)",

  // Parchment, used by the decree card and the Hogwarts letter
  parchment: "#f5e6c8",
  parchmentDeep: "#e8d4a8",
  parchmentEdge: "#c4942a",
  ink: "#2a1a0a",
  inkFaded: "#6b4a1a",

  // Candlelight
  gold: "#ffd700",
  goldDim: "rgba(255, 215, 0, 0.15)",
  waxRed: "#a00000",
  waxDeep: "#5a0000",

  textPrimary: "#e8e4e0",
  textSecondary: "#c4a882",
  textMuted: "#7a6b5d",

  border: "rgba(196, 148, 42, 0.12)",
  borderHover: "rgba(196, 148, 42, 0.35)",
};

export const fonts = {
  spell: "'Cinzel', serif",
  body: "'Raleway', sans-serif",
  mono: "'JetBrains Mono', monospace",
  hand: "'Caveat', cursive",
  letter: "'Crimson Text', serif",
};

export const buildTheme = (house: HouseName) => ({
  house: HOUSES[house],
  houseKey: house,
  colors: castle,
  fonts,
});

export type Theme = ReturnType<typeof buildTheme>;

/* Kept so older imports of `theme` keep resolving. */
export const theme = buildTheme("gryffindor");
